import React, { useState, useEffect, useCallback } from 'react';
import {
  Upload, FileSpreadsheet, Trash2, Eye, CheckCircle, AlertCircle, X
} from 'lucide-react';
import { uploadDataset, getDatasets, deleteDataset, getDatasetPreview } from '../services/api';
import { format } from 'date-fns';

function DataUpload() {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    loadDatasets();
  }, []);

  const loadDatasets = async () => {
    try {
      setLoading(true);
      const response = await getDatasets();
      setDatasets(response.datasets || []);
    } catch (err) {
      console.error('Error loading datasets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleUpload(file);
  };

  const handleUpload = async (file) => {
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const extension = '.' + file.name.split('.').pop().toLowerCase();

    if (!validTypes.includes(extension)) {
      setError('Please upload a CSV or Excel file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const response = await uploadDataset(file);

      setSuccess(`Successfully uploaded "${response.dataset.name}" with ${response.dataset.rowCount} rows`);
      loadDatasets();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      await deleteDataset(id);
      setSuccess(`Dataset "${name}" deleted successfully`);
      loadDatasets();
    } catch (err) {
      setError('Failed to delete dataset');
    }
  };

  const handlePreview = async (dataset) => {
    try {
      setSelectedDataset(dataset);
      const response = await getDatasetPreview(dataset._id, 50);
      setPreviewData(response);
    } catch (err) {
      setError('Failed to load preview');
    }
  };

  const closePreview = () => {
    setSelectedDataset(null);
    setPreviewData(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="data-upload">
      <div className="page-header">
        <h2>Upload Data</h2>
        <p>Upload CSV or Excel files to analyze and generate forecasts</p>
      </div>

      {/* Notifications */}
      {error && (
        <div className="notification error" style={{
          background: '#fee2e2',
          color: '#991b1b',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AlertCircle size={20} />
            {error}
          </div>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>
      )}

      {success && (
        <div className="notification success" style={{
          background: '#d1fae5',
          color: '#065f46',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CheckCircle size={20} />
            {success}
          </div>
          <button onClick={() => setSuccess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>
      )}

      {/* Upload Zone */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div
          className={`upload-zone ${dragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            type="file"
            id="file-input"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {uploading ? (
            <div className="spinner"></div>
          ) : (
            <>
              <Upload className="upload-zone-icon" />
              <h3>Drag and drop your file here</h3>
              <p>or click to browse. Supports CSV, XLSX, XLS (max 50MB)</p>
            </>
          )}
        </div>
      </div>

      {/* Datasets List */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Uploaded Datasets</h3>
          <span className="badge badge-info">{datasets.length} files</span>
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : datasets.length === 0 ? (
          <div className="empty-state">
            <FileSpreadsheet size={64} />
            <p>No datasets uploaded yet</p>
            <p style={{ fontSize: '0.875rem' }}>Upload a CSV or Excel file to get started</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Rows</th>
                  <th>Columns</th>
                  <th>Date Column</th>
                  <th>Status</th>
                  <th>Uploaded</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {datasets.map((dataset) => (
                  <tr key={dataset._id}>
                    <td style={{ fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileSpreadsheet size={18} color="#6b7280" />
                        {dataset.name}
                      </div>
                    </td>
                    <td>{dataset.rowCount?.toLocaleString()}</td>
                    <td>{dataset.columns?.length}</td>
                    <td>{dataset.metadata?.dateColumn || '-'}</td>
                    <td>
                      <span className={`badge badge-${dataset.status === 'ready' ? 'success' : 'warning'}`}>
                        {dataset.status}
                      </span>
                    </td>
                    <td>{format(new Date(dataset.createdAt), 'MMM d, yyyy')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handlePreview(dataset)}
                          style={{ padding: '0.375rem 0.75rem' }}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDelete(dataset._id, dataset.name)}
                          style={{ padding: '0.375rem 0.75rem' }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {selectedDataset && previewData && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            width: '100%',
            maxWidth: '1000px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ fontWeight: 600 }}>{selectedDataset.name}</h3>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Showing {previewData.preview?.length} of {previewData.totalRows} rows
                </p>
              </div>
              <button
                onClick={closePreview}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>
            <div style={{ overflow: 'auto', flex: 1 }}>
              <table style={{ fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    {previewData.columns?.map((col, i) => (
                      <th key={i}>
                        <div>{col.name}</div>
                        <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#9ca3af' }}>
                          {col.type}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.preview?.map((row, i) => (
                    <tr key={i}>
                      {previewData.columns?.map((col, j) => (
                        <td key={j}>{String(row[col.name] ?? '-')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataUpload;
